import { useState, useRef, useEffect } from "react";
import { X, Send } from "lucide-react";
import api from "@/lib/api";

const SUGGESTIONS = [
  "Which shipments are delayed?",
  "Which vehicles are available in Johannesburg?",
  "Show today's deliveries",
  "Which shipments require immediate attention?",
];

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Yo! I'm Ebik Roadman, your logistics sidekick. Ask me about shipments, vehicles, deliveries or costs." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [bubble, setBubble] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    (async () => {
      let cadence = "login";
      try {
        const p = await api.get("/notifications/prefs");
        cadence = p.data.bubble_cadence || "login";
      } catch { return; }
      const today = new Date().toISOString().slice(0, 10);
      if (cadence === "daily") {
        if (localStorage.getItem("roadman_greeted_date") === today) return;
      } else if (sessionStorage.getItem("roadman_greeted")) {
        return;
      }
      try {
        const { data } = await api.get("/ops/predictive-eta");
        const s = data.summary || {};
        const flagged = (s.high || 0) + (s.medium || 0);
        if (flagged > 0) setBubble(`Howzit! ${flagged} shipment${flagged > 1 ? "s" : ""} need eyes today — ${s.high || 0} high risk.`);
      } catch { /* no access */ }
      if (cadence === "daily") localStorage.setItem("roadman_greeted_date", today);
      else sessionStorage.setItem("roadman_greeted", "1");
    })();
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", { question: q });
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "I couldn't reach the intelligence service right now." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && bubble && (
        <div data-testid="roadman-bubble" className="fixed bottom-24 right-6 z-50 w-[268px] rounded-2xl border border-border bg-card shadow-xl p-3.5">
          <div className="flex items-start gap-2">
            <img src="/roadman.png" alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
            <p className="text-sm text-foreground flex-1">{bubble}</p>
            <button onClick={() => setBubble(null)} data-testid="roadman-bubble-close" className="text-muted-foreground hover:text-foreground shrink-0"><X size={14} /></button>
          </div>
          <button onClick={() => { setOpen(true); setBubble(null); }} data-testid="roadman-bubble-open"
            className="mt-2.5 w-full text-xs font-semibold rounded-lg bg-primary text-white py-1.5 hover:bg-red-700 transition-colors">
            Ask Roadman
          </button>
        </div>
      )}
      {!open && (
        <button data-testid="ai-assistant-open" onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-primary pl-2 pr-5 py-2.5 text-white shadow-xl shadow-red-600/30 hover:bg-red-700 transition-colors">
          <img src="/roadman.png" alt="Ebik Roadman" className="w-9 h-9 rounded-full object-cover bg-white/20" /> <span className="font-semibold text-sm">Ebik Roadman</span>
        </button>
      )}
      {open && (
        <div data-testid="ai-assistant-panel" className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-[400px] h-[560px] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-[#0B0F17] text-white">
            <div className="flex items-center gap-2">
              <img src="/roadman.png" alt="Ebik Roadman" className="w-9 h-9 rounded-lg object-cover bg-primary/20" />
              <div className="leading-tight">
                <div className="font-heading font-bold text-sm">Ebik Roadman</div>
                <div className="text-[10px] text-emerald-400 font-mono">● Online · here to help</div>
              </div>
            </div>
            <button data-testid="ai-assistant-close" onClick={() => setOpen(false)} className="opacity-70 hover:opacity-100"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-white rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && <div className="text-xs text-muted-foreground font-mono px-1">Ebik Roadman is thinking…</div>}
            {messages.length <= 1 && (
              <div className="pt-2 space-y-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} data-testid="ai-suggestion"
                    className="block w-full text-left text-xs px-3 py-2 rounded-lg border border-border hover:border-primary hover:text-primary transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-border flex gap-2">
            <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about your logistics…"
              className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            <button data-testid="ai-send" onClick={() => send()} disabled={loading}
              className="rounded-lg bg-primary text-white px-3 hover:bg-red-700 disabled:opacity-50"><Send size={16} /></button>
          </div>
        </div>
      )}
    </>
  );
}
